import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { maintenanceEnabled, loadMaintenanceStatus } from '../stores/maintenance'
import type { ModuleName, ActionName } from '../composables/usePermissions'

// Types pour les meta des routes
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresGuest?: boolean
    requiresSuperAdmin?: boolean
    requiresOmValidationAccess?: boolean
    permission?: {
      action: ActionName
      module: ModuleName
    }
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../pages/LoginPage.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../pages/ResetPasswordPage.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/new-password',
      name: 'new-password',
      component: () => import('../pages/NewPasswordPage.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('../pages/ChangePasswordPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/acces-refuse',
      name: 'access-denied',
      component: () => import('../pages/AccessDeniedPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('../pages/DashboardPage.vue'),
      meta: { requiresAuth: true }
    },
    // Dev / Tests (module: dev)
    {
      path: '/dev/components',
      name: 'components-showcase',
      component: () => import('../pages/ComponentsShowcase.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/lots-test',
      name: 'lots-test',
      component: () => import('../pages/test/LotsTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/charts-test',
      name: 'charts-test',
      component: () => import('../pages/test/ChartsTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/pdf-viewer',
      name: 'pdf-viewer-test',
      component: () => import('../pages/tests/PdfViewerTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/calendar',
      name: 'calendar-test',
      component: () => import('../pages/dev/CalendarTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/mail-test',
      name: 'mail-test',
      component: () => import('../pages/dev/MailTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/milestones',
      name: 'dev-milestones',
      component: () => import('../pages/dev/MilestonesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/laboxy',
      name: 'laboxy-test',
      component: () => import('../pages/dev/LaboxyTestPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/laboxy-times',
      name: 'laboxy-times',
      component: () => import('../pages/dev/LaboxyTimesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/laboxy-tasks',
      name: 'laboxy-tasks',
      component: () => import('../pages/dev/LaboxyTasksPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/laboxy-projects-list',
      name: 'laboxy-projects-list',
      component: () => import('../pages/dev/LaboxyProjectsListPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/laboxy-timemanagement',
      name: 'laboxy-timemanagement',
      component: () => import('../pages/dev/LaboxyTimeManagementPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    {
      path: '/dev/devis-terms-preview',
      name: 'devis-terms-preview',
      component: () => import('../pages/dev/DevisTermsPreviewPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'dev' } }
    },
    // RH (module: hr)
    {
      path: '/rh/utilisateurs',
      name: 'rh-utilisateurs',
      component: () => import('../pages/rh/UtilisateursPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'hr' } }
    },
    {
      path: '/rh/utilisateurs/creer',
      name: 'rh-utilisateur-creer',
      component: () => import('../pages/rh/CreerUtilisateurPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'create', module: 'hr' } }
    },
    {
      path: '/rh/utilisateurs/:id',
      name: 'rh-utilisateur-detail',
      component: () => import('../pages/rh/UtilisateurDetailPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'hr' } }
    },
    {
      path: '/rh/messages',
      name: 'rh-messages',
      component: () => import('../pages/rh/MessagesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'edit', module: 'hr' } } // Niveau 3+ (Cell Lead)
    },
    // Projets (module: project)
    {
      path: '/projets/opportunites/creer',
      name: 'projets-opportunites-creer',
      component: () => import('../pages/projets/CreerOpportunitePage.vue'),
      meta: { requiresAuth: true, permission: { action: 'create', module: 'project' } }
    },
    {
      path: '/projets/:id/editer',
      name: 'projet-editer',
      component: () => import('../pages/projets/EditerProjetPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'edit', module: 'project' } }
    },
    {
      path: '/projets/liste',
      name: 'projets-liste',
      component: () => import('../pages/projets/ProjetsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'project' } }
    },
    {
      path: '/projets/:id',
      name: 'projet-detail',
      component: () => import('../pages/projets/ProjetDetailPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'project' } }
    },
    {
      path: '/support/encours',
      name: 'support-encours',
      component: () => import('../pages/support/EncoursPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'edit', module: 'project' } } // Niveau 3+ (Cell Lead)
    },
    {
      path: '/projets/suivi',
      name: 'projets-suivi',
      component: () => import('../pages/projets/SuiviProjetPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'edit', module: 'project' } } // Niveau 3+ (Cell Lead)
    },
    // Sociétés (module: company)
    {
      path: '/societes/liste',
      name: 'societes-liste',
      component: () => import('../pages/societes/SocietesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'company' } }
    },
    {
      path: '/societes/contacts',
      name: 'societes-contacts',
      component: () => import('../pages/societes/ContactsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'company' } }
    },
    // Facturation (module: finance)
    {
      path: '/facturation/suivi',
      name: 'facturation-suivi',
      component: () => import('../pages/facturation/SuiviFacturationPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'finance' } }
    },
    {
      path: '/facturation/archives',
      name: 'facturation-archives',
      component: () => import('../pages/facturation/ArchivesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'finance' } }
    },
    {
      path: '/facturation/echeanciers',
      name: 'facturation-echeanciers',
      component: () => import('../pages/facturation/EcheanciersPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'finance' } } // Niveau 2+ (Cellule)
    },
    // Support (module: support)
    {
      path: '/support/gerer-projets',
      name: 'support-gerer-projets',
      component: () => import('../pages/support/GererProjetsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/parametres-devis',
      name: 'support-parametres-devis',
      component: () => import('../pages/support/ParametresDevisPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/materiels',
      name: 'support-materiels',
      component: () => import('../pages/support/MaterielsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/laboratoires',
      name: 'support-laboratoires',
      component: () => import('../pages/support/LaboratoiresPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/cellules',
      name: 'support-cellules',
      component: () => import('../pages/support/CellulesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/themes',
      name: 'support-themes',
      component: () => import('../pages/support/ThemesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/regles-notification',
      name: 'support-regles-notification',
      component: () => import('../pages/support/NotificationRulesPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/support/historique',
      name: 'support-historique',
      component: () => import('../pages/support/HistoriqueProjetsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'support' } }
    },
    {
      path: '/docs',
      name: 'documents',
      component: () => import('../pages/DocumentsPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'wiki' } }
    },
    // Mon Dashboard (dashboards personnalises)
    {
      path: '/my-dashboard',
      name: 'my-dashboard',
      component: () => import('../pages/MyDashboardPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/my-dashboard/creer',
      name: 'my-dashboard-creer',
      component: () => import('../pages/MyDashboardPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/my-dashboard/:slug',
      name: 'my-dashboard-edit',
      component: () => import('../pages/MyDashboardEditPage.vue'),
      meta: { requiresAuth: true }
    },
    // Ordres de mission (module: mission_order)
    // Note: Pour mission_order, niveau 1+ peut créer (contrairement aux autres modules où create = niveau 3+)
    {
      path: '/om/creer',
      name: 'om-creer',
      component: () => import('../pages/om/CreerOmPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'mission_order' } }
    },
    {
      path: '/om/:id',
      name: 'om-view',
      component: () => import('../pages/om/CreerOmPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'mission_order' } }
    },
    {
      path: '/om/liste',
      name: 'om-liste',
      component: () => import('../pages/om/ListeOmPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'view', module: 'mission_order' } }
    },
    {
      path: '/om/validation',
      name: 'om-validation',
      component: () => import('../pages/om/ValidationOmPage.vue'),
      // Réservé aux Cellule Lead / RH / PDG / admin (cf. canAccessOmValidation)
      meta: { requiresAuth: true, requiresOmValidationAccess: true }
    },
    {
      path: '/om/archives',
      name: 'om-archives',
      component: () => import('../pages/om/ArchivesOmPage.vue'),
      meta: { requiresAuth: true, permission: { action: 'edit', module: 'mission_order' } } // Niveau 3+ (Cell Lead)
    },
    // Admin (super admin uniquement)
    {
      path: '/admin/projet',
      name: 'admin-projet',
      component: () => import('../pages/admin/AdminProjetPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/widgets',
      name: 'admin-widgets',
      component: () => import('../pages/admin/AdminWidgetsPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/sync',
      name: 'admin-sync',
      component: () => import('../pages/admin/AdminSyncPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/statistiques',
      name: 'admin-stats',
      component: () => import('../pages/admin/AdminStatsPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/notifications',
      name: 'admin-notifications',
      component: () => import('../pages/admin/AdminNotificationsPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/liens-rapides',
      name: 'admin-quick-links',
      component: () => import('../pages/admin/AdminQuickLinksPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    // Création de widget désactivée : les widgets sont des composants dédiés,
    // ajoutés sur demande. Route conservée en commentaire pour réactivation.
    // {
    //   path: '/admin/widgets/creer',
    //   name: 'admin-widget-creer',
    //   component: () => import('../pages/admin/AdminWidgetEditPage.vue'),
    //   meta: { requiresAuth: true, requiresSuperAdmin: true }
    // },
    {
      path: '/admin/widgets/:id',
      name: 'admin-widget-edit',
      component: () => import('../pages/admin/AdminWidgetEditPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('../pages/MaintenancePage.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../pages/NotFoundPage.vue'),
    }
  ]
})

// Helper pour vérifier les permissions (logique dupliquée du composable pour éviter les problèmes de setup context)
function checkPermission(authStore: ReturnType<typeof useAuthStore>, action: ActionName, module: ModuleName): boolean {
  const roles = authStore.user?.roles
  if (!roles) return false

  // Super admin bypass (sauf si simulation active)
  if (roles.is_super_admin && !roles.simulation_active) {
    return true
  }

  // Utiliser les permissions pré-calculées si disponibles
  if (roles.permissions?.[module]) {
    const perms = roles.permissions[module]
    switch (action) {
      case 'view': return perms.canView
      case 'create': return perms.canCreate
      case 'edit': return perms.canEdit
      case 'delete': return perms.canDelete
      case 'export': return perms.canExport
    }
  }

  // Fallback: calcul local
  const level = roles[module] ?? 0
  const PERSONAL = 1
  const CELLULE_LEAD = 3
  const GLOBAL = 4

  // Dev : super user uniquement (pas même Global)
  if (module === 'dev') {
    return false // Seuls les super admins passent (bypass plus haut)
  }

  // Support : niveau Global (4) uniquement
  if (module === 'support') {
    return level >= GLOBAL
  }

  switch (action) {
    case 'view':
      return level >= PERSONAL
    case 'create':
    case 'export':
    case 'edit':
    case 'delete':
      return level >= CELLULE_LEAD
    default:
      return false
  }
}

// Helper : accès à la vue Validation des OMs
// Cellule Lead (scope mission_order >= 3), RH, PDG, GLOBAL ou super admin.
function canAccessOmValidation(authStore: ReturnType<typeof useAuthStore>): boolean {
  const roles = authStore.user?.roles
  if (!roles) return false
  if (roles.is_super_admin && !roles.simulation_active) return true
  if (roles.is_pdg) return true
  if (roles.is_rh) return true
  if ((roles.mission_order ?? 0) >= 3) return true
  return !!roles.lead_cellule_ids?.length
}

// Helper pour vérifier si l'utilisateur est super admin (sans simulation)
function isSuperAdmin(authStore: ReturnType<typeof useAuthStore>): boolean {
  const roles = authStore.user?.roles
  if (!roles) return false
  return roles.is_super_admin && !roles.simulation_active
}

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  await loadMaintenanceStatus()

  // Vérifier l'authentification au démarrage si pas encore fait
  if (authStore.user === null && !authStore.loading) {
    await authStore.checkAuth()
  }

  // Routes publiques (accessibles même en maintenance)
  const PUBLIC_ROUTES = ['login', 'reset-password', 'new-password', 'not-found', 'maintenance']
  const routeName = to.name as string
  
  const MAINTENANCE_ENABLED = maintenanceEnabled.value

  // Logique de maintenance
  if (MAINTENANCE_ENABLED && !PUBLIC_ROUTES.includes(routeName)) {
    if (!authStore.isAuthenticated) {
      // Laisser accéder au login pour s'authentifier
      if (routeName === 'login') {
        next()
        return
      }
      // Rediriger vers login pour s'authentifier
      next({ name: 'login' })
      return
    }
    
    // Si authentifié, vérifier si super admin
    if (!isSuperAdmin(authStore)) {
      // Non super admin : laisser accéder à la page de maintenance
      next()
      return
    }
  }

  const requiresAuth = to.meta.requiresAuth
  const requiresGuest = to.meta.requiresGuest
  const requiresSuperAdmin = to.meta.requiresSuperAdmin
  const requiresOmValidationAccess = to.meta.requiresOmValidationAccess
  const permission = to.meta.permission

  if (requiresAuth && !authStore.isAuthenticated) {
    // Rediriger vers login si non authentifié
    next({ name: 'login' })
  } else if (requiresGuest && authStore.isAuthenticated) {
    // Rediriger vers dashboard si déjà authentifié
    next({ name: 'dashboard' })
  } else if (requiresSuperAdmin && !isSuperAdmin(authStore)) {
    // Rediriger vers page accès refusé si pas super admin
    next({ name: 'access-denied' })
  } else if (requiresOmValidationAccess && !canAccessOmValidation(authStore)) {
    // Réservé aux Cellule Lead / RH / PDG / admin
    next({ name: 'access-denied' })
  } else if (permission && !checkPermission(authStore, permission.action, permission.module)) {
    // Rediriger vers page accès refusé si permissions insuffisantes
    next({ name: 'access-denied' })
  } else {
    next()
  }
})

// Mesure d'usage interne : une page vue par navigation aboutie sur une page
// authentifiée. Fire-and-forget (sendBeacon) — jamais bloquant pour l'UX,
// restitution agrégée dans /admin/statistiques.
router.afterEach((to) => {
  if (!to.meta.requiresAuth || !to.name) return
  const payload = JSON.stringify({ route_name: String(to.name), path: to.fullPath })
  try {
    navigator.sendBeacon('/api/analytics/pageview', new Blob([payload], { type: 'application/json' }))
  } catch {
    // Le tracking ne doit jamais faire échouer la navigation.
  }
})

export default router
